/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM 
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { EMPTY, Subject, merge, of, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { Filter } from 'app/shared/models/filter.model';
import { FilterFieldsBaseComponent } from '../list-filter/filter-fields-base.component';
import {
	ActiveFilterLozenge, DEFAULT_FILTER_HEADER_OPTIONS, DEFAULT_FILTER_LAYOUT_OPTIONS, FilterApplyMode,
	FilterFieldMetadata, FilterHeaderMode, FilterHeaderOptions, FilterLayoutOptions, FilterPersistenceMode,
	FilterPersistenceOptions, valuesEqual,
} from './filter-framework.models';
import { FilterPersistenceService } from './filter-persistence.service';

@Component({
	selector: 'orbit-filter-framework',
	templateUrl: './filter-framework.component.html',
	styleUrls: ['./filter-framework.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class FilterFrameworkComponent implements AfterContentInit, OnChanges {
	@Input() entityLabel = '';
	@Input() singularEntityLabel = '';
	@Input() pluralEntityLabel = '';
	@Input() filteredCount: number | null = null;
	@Input() totalCount: number | null = null;
	@Input() loading = false;
	@Input() hasError = false;
	@Input() disabled = false;
	@Input() headerOptions: Partial<FilterHeaderOptions> = {};
	@Input() layoutOptions: Partial<FilterLayoutOptions> = {};
	@Input() persistenceOptions: FilterPersistenceOptions = { enabled: false, mode: FilterPersistenceMode.None, storageKey: '', schemaVersion: 1, persistExpandedState: false };
	@Input() persistenceEnabled = false;
	@Input() clearFiltersLabel = 'Reset Filters';
	@ContentChild(FilterFieldsBaseComponent, { static: true }) filterFields!: FilterFieldsBaseComponent;
	@Output() expandedChange = new EventEmitter<boolean>();
	@Output() activeFilterRemoved = new EventEmitter<string>();
	@Output() clearFilters = new EventEmitter<void>();
	@Output() filterChanged = new EventEmitter<Filter[]>();
	@Output() persistenceEnabledChange = new EventEmitter<boolean>();

	options!: FilterHeaderOptions;
	layout!: FilterLayoutOptions;
	expanded = true;
	lozenges: ActiveFilterLozenge[] = [];
	private activeForm!: FormGroup;
	private metadata: FilterFieldMetadata[] = [];
	private suppress = false;
	private readonly cancelPendingApply$ = new Subject<void>();
	private readonly persist$ = new Subject<void>();
	private readonly destroyRef = inject(DestroyRef);

	constructor(private persistenceService: FilterPersistenceService, private cdr: ChangeDetectorRef) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.headerOptions && this.options) this.options = { ...DEFAULT_FILTER_HEADER_OPTIONS, ...this.headerOptions };
		if (changes.layoutOptions && this.layout) this.layout = { ...DEFAULT_FILTER_LAYOUT_OPTIONS, ...this.layoutOptions };
		this.cdr.markForCheck();
	}

	ngAfterContentInit(): void {
		this.options = { ...DEFAULT_FILTER_HEADER_OPTIONS, ...this.headerOptions };
		this.layout = { ...DEFAULT_FILTER_LAYOUT_OPTIONS, ...this.layoutOptions };
		this.expanded = this.options.startExpanded;
		this.activeForm = this.filterFields?.dataForm;
		this.metadata = this.filterFields?.filterMetadata ?? [];
		if (!this.activeForm) throw new Error('orbit-filter-framework requires a projected FilterFieldsBaseComponent.');
		if (!this.metadata.length) throw new Error('orbit-filter-framework requires explicit consumer filter metadata.');
		this.assertMetadataMatchesForm();
		this.restorePersistedState();
		this.updateLozenges();
		this.subscribeToFiltering();
		this.subscribeToPresentationState();
		this.filterFields.filterChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyCurrentFilters());
	}

	get heading(): string {
		if (!this.options || this.options.mode === FilterHeaderMode.Hidden) return '';
		if (this.options.mode === FilterHeaderMode.Filters) return 'FILTERS';
		if (this.options.mode === FilterHeaderMode.EntityFilters) return `${(this.singularEntityLabel || this.entityLabel).toUpperCase()} FILTERS`;
		return (this.entityLabel || this.pluralEntityLabel).toUpperCase();
	}

	get resultEntityLabel(): string {
		return this.filteredCount === 1 ? (this.singularEntityLabel || this.entityLabel) : (this.pluralEntityLabel || this.entityLabel);
	}

	get gridStyle(): Record<string, string> {
		return { '--filter-desktop-columns': String(this.layout?.desktopColumns ?? 12), '--filter-tablet-columns': String(this.layout?.tabletColumns ?? 12), '--filter-mobile-columns': String(this.layout?.mobileColumns ?? 12) };
	}

	toggleExpanded(): void {
		if (!this.options.allowCollapse || this.disabled) return;
		this.expanded = !this.expanded;
		this.expandedChange.emit(this.expanded);
		this.persist$.next();
	}

	remove(item: ActiveFilterLozenge): void {
		if (this.disabled || !item.removable) return;
		const definition = this.metadata.find(field => field.id === item.id);
		if (!definition) return;
		this.cancelPendingApply$.next();
		this.suppress = true;
		this.restoreMetadataDefault(definition);
		this.filterFields.onFilterFieldReset(definition.id);
		this.suppress = false;
		this.activeFilterRemoved.emit(item.id);
		this.applyCurrentFilters();
	}

	clear(): void {
		if (this.disabled || !this.lozenges.length) return;
		this.cancelPendingApply$.next();
		this.suppress = true;
		const hasConsumerReset = this.filterFields?.resetToDefault !== FilterFieldsBaseComponent.prototype.resetToDefault;
		if (hasConsumerReset) this.filterFields.resetToDefault();
		else this.metadata.forEach(item => this.restoreMetadataDefault(item));
		this.suppress = false;
		this.persistenceService.clear(this.persistenceOptions);
		this.clearFilters.emit();
		this.applyCurrentFilters();
	}

	onRememberChanged(enabled: boolean): void {
		this.persistenceEnabled = enabled;
		this.persistenceEnabledChange.emit(enabled);
		if (enabled) this.persist(); else this.persistenceService.clear(this.persistenceOptions);
	}

	trackLozenge(_: number, item: ActiveFilterLozenge): string { return item.id; }

	private subscribeToFiltering(): void {
		const changes = this.metadata.flatMap(item => item.controlNames.map(controlName =>
			this.activeForm.get(controlName)!.valueChanges.pipe(distinctUntilChanged(valuesEqual), map(() => item)),
		));
		merge(...changes, this.cancelPendingApply$.pipe(map((): FilterFieldMetadata | null => null))).pipe(
			switchMap(item => {
				if (!item) return EMPTY;
				const delay = item.applyMode === FilterApplyMode.Debounced ? item.debounceMs ?? 300 : 0;
				return delay > 0 ? timer(delay).pipe(map(() => item)) : of(item);
			}),
			filter(() => !this.suppress && !this.disabled && this.activeForm.valid),
			takeUntilDestroyed(this.destroyRef),
		).subscribe(() => this.applyCurrentFilters());
	}

	private subscribeToPresentationState(): void {
		this.activeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			if (this.suppress) return;
			this.updateLozenges();
			this.persist$.next();
			this.cdr.markForCheck();
		});
		this.persist$.pipe(switchMap(() => timer(300)), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.persist());
	}

	private restorePersistedState(): void {
		const restored = this.persistenceService.read(this.persistenceOptions, Object.keys(this.activeForm.controls));
		if (!restored) return;
		this.suppress = true;
		this.activeForm.patchValue(this.validRestoredValues(restored.values), { emitEvent: false });
		if (this.persistenceOptions.persistExpandedState && restored.expanded != null) this.expanded = restored.expanded;
		this.suppress = false;
	}

	private applyCurrentFilters(): void {
		if (!this.activeForm.valid) return;
		this.updateLozenges();
		this.persist$.next();
		this.filterChanged.emit(this.filterFields.getFilters());
		this.filterFields.markPristine();
		this.cdr.markForCheck();
	}

	private updateLozenges(): void {
		this.lozenges = this.metadata
			.filter(item => item.isActive(this.metadataValue(item), item.defaultValue))
			.map(item => {
				const value = this.metadataValue(item);
				const formattedValue = item.formatDisplayValue(value);
				return {
					id: item.id,
					label: item.label,
					value,
					formattedValue,
					removable: item.removable !== false,
					accessibleRemoveLabel: `Remove ${item.label} filter: ${formattedValue}`,
				};
			})
			.filter(item => !this.isAllSelectionLabel(item.formattedValue) && !this.isAllSelectionValue(item.value))
			.map(({ value: _, ...item }) => item);
	}

	private isAllSelectionLabel(value: string): boolean {
		const normalized = value.trim();
		return /^all(\b|\s)/i.test(normalized);
	}

	private isAllSelectionValue(value: unknown): boolean {
		if (typeof value === 'string') {
			return value.trim().toLowerCase() === 'all';
		}

		if (Array.isArray(value)) {
			return value.includes(0);
		}

		if (value && typeof value === 'object') {
			return Object.values(value as Record<string, unknown>).some(item => this.isAllSelectionValue(item));
		}

		return false;
	}

	private metadataValue(item: FilterFieldMetadata): unknown {
		if (item.controlNames.length === 1) return this.activeForm.get(item.controlNames[0])?.value;
		return Object.fromEntries(item.controlNames.map(name => [name, this.activeForm.get(name)?.value]));
	}

	private restoreMetadataDefault(item: FilterFieldMetadata): void {
		if (item.controlNames.length === 1) {
			this.activeForm.get(item.controlNames[0])?.setValue(this.cloneValue(item.defaultValue), { emitEvent: false });
			return;
		}
		const defaults = item.defaultValue as Record<string, unknown>;
		item.controlNames.forEach(name => this.activeForm.get(name)?.setValue(this.cloneValue(defaults[name]), { emitEvent: false }));
	}

	private persist(): void {
		if (!this.persistenceEnabled || !this.persistenceOptions.enabled || this.persistenceOptions.mode === FilterPersistenceMode.None) return;
		const eligibleNames = new Set(this.metadata.filter(item => item.persist !== false && !(this.persistenceOptions.mode === FilterPersistenceMode.Local && item.sensitive)).flatMap(item => item.controlNames));
		const values = Object.fromEntries(Object.entries(this.activeForm.getRawValue()).filter(([name]) => eligibleNames.has(name)));
		this.persistenceService.write(this.persistenceOptions, values, this.expanded);
	}

	private validRestoredValues(values: Record<string, unknown>): Record<string, unknown> {
		const valid: Record<string, unknown> = {};
		for (const item of this.metadata) {
			if (item.controlNames.length === 1) {
				const name = item.controlNames[0];
				if (name in values && (!item.validateRestoredValue || item.validateRestoredValue(values[name]))) valid[name] = values[name];
				continue;
			}
			const grouped = Object.fromEntries(item.controlNames.filter(name => name in values).map(name => [name, values[name]]));
			if (Object.keys(grouped).length && (!item.validateRestoredValue || item.validateRestoredValue(grouped))) Object.assign(valid, grouped);
		}
		return valid;
	}

	private assertMetadataMatchesForm(): void {
		const missing = this.metadata.flatMap(item => item.controlNames).filter(name => !this.activeForm.contains(name));
		if (missing.length) throw new Error(`Filter metadata references unknown controls: ${missing.join(', ')}`);
	}

	private cloneValue<T>(value: T): T {
		if (value instanceof Date) return new Date(value.getTime()) as T;
		if (Array.isArray(value)) return value.map(item => this.cloneValue(item)) as T;
		if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.cloneValue(item)])) as T;
		return value;
	}
}
